import { zodResolver } from "@hookform/resolvers/zod";
import {
  CheckCircle,
  Lock,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  InputAdornment,
  Link,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { changePassword, setPassword } from "../../../services/authService";
import { useAuthStore } from "../../../store/useAuthStore";
import { formatApiError } from "../../../utils/formatApiError";
import {
  ChangePasswordFormData,
  getChangePasswordSchema,
  SetPasswordFormData,
  getSetPasswordSchema,
} from "../../../validation/authSchema";

const ProfileChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const { user, setToken, setUser, logout } = useAuthStore();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  // Determine account type from JWT token (authType is now in JWT)
  // Backend uses "google" for AuthType, frontend might use "gmail" - check both
  const isGoogleUser =
    user?.authType === "google" || user?.authType === "gmail";

  // For Google users: check HasSetPassword flag from JWT
  // - If HasSetPassword = false → show "Set Password" (has dummy password)
  // - If HasSetPassword = true → show "Change Password" (has real password)
  // For manual users: always show "Change Password" (they always have password)
  const isGoogleUserWithoutPassword =
    isGoogleUser && !(user?.hasSetPassword ?? false);

  // Use appropriate schema based on user type
  const schema = isGoogleUserWithoutPassword
    ? getSetPasswordSchema()
    : getChangePasswordSchema();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<ChangePasswordFormData | SetPasswordFormData>({
    resolver: zodResolver(schema),
  });

  // Type-safe error access for currentPassword (only exists in ChangePasswordFormData)
  const currentPasswordError = !isGoogleUserWithoutPassword
    ? (errors as any).currentPassword
    : undefined;

  // Check if user has password (we'll infer from authType for now)
  // For Google users, if they don't have password, show "Set Password"
  // For manual users or Google users with password, show "Change Password"

  const showSnackbar = (message: string, severity: "success" | "error") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const onSubmit = async (
    data: ChangePasswordFormData | SetPasswordFormData
  ) => {
    // For Google users without password, set password directly
    // They're already authenticated (logged in with Google), so no need for re-auth
    if (isGoogleUserWithoutPassword) {
      setIsLoading(true);
      try {
        const setPasswordData = data as SetPasswordFormData;
        // User is already authenticated via JWT (logged in with Google)
        // No need for Google re-authentication - don't send googleIdToken
        const response = await setPassword({
          newPassword: setPasswordData.newPassword,
          confirmNewPassword: setPasswordData.confirmNewPassword,
        });

        if (response.isSuccess) {
          setIsSuccess(true);
          showSnackbar(t("profile.passwordSetSuccess"), "success");
          reset();

          // If backend returns new token, update it and refresh user object
          if (response.data?.token) {
            setToken(response.data.token);
            // Decode new token to update user object with HasSetPassword=true
            const { decodeJwtToken } = await import("../../../utils/jwtUtils");
            const decodedToken = decodeJwtToken(response.data.token);
            if (decodedToken && user) {
              // Update user object with new hasSetPassword value from token
              setUser({
                ...user,
                hasSetPassword: decodedToken.hasSetPassword ?? true,
              });
            }
          }
        } else {
          showSnackbar(formatApiError(response), "error");
        }
      } catch (error) {
        const errorMessage = formatApiError(error);
        showSnackbar(errorMessage, "error");
      } finally {
        setIsLoading(false);
      }
    } else {
      // Change password for users with existing password
      setIsLoading(true);
      try {
        const changePasswordData = data as ChangePasswordFormData;
        const response = await changePassword({
          currentPassword: changePasswordData.currentPassword,
          newPassword: changePasswordData.newPassword,
          confirmNewPassword: changePasswordData.confirmNewPassword,
        });

        if (response.isSuccess) {
          setIsSuccess(true);
          showSnackbar(t("profile.passwordChangedSuccess"), "success");
          reset();
        } else {
          showSnackbar(formatApiError(response), "error");
        }
      } catch (error) {
        const errorMessage = formatApiError(error);
        showSnackbar(errorMessage, "error");
      } finally {
        setIsLoading(false);
      }
    }
  };


  if (isSuccess) {
    return (
      <Box className="notranslate" translate="no">
        <Typography
          variant="h5"
          sx={{ fontWeight: 600, mb: 3 }}
          className="notranslate"
          translate="no"
        >
          {isGoogleUserWithoutPassword
            ? t("profile.setPasswordTitle")
            : t("profile.changePasswordTitle")}
        </Typography>
        <Card>
          <CardContent sx={{ textAlign: "center", py: 4 }}>
            <CheckCircle sx={{ fontSize: 64, color: "success.main", mb: 2 }} />
            <Typography
              variant="h6"
              gutterBottom
              className="notranslate"
              translate="no"
            >
              {isGoogleUserWithoutPassword
                ? t("profile.passwordSetSuccess")
                : t("profile.passwordChangedSuccess")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mb: 3 }}
              className="notranslate"
              translate="no"
            >
              {isGoogleUserWithoutPassword
                ? t("profile.canNowLoginWithEmail")
                : t("profile.passwordChangedPleaseUseNew")}
            </Typography>
            <Button
              variant="contained"
              className="notranslate"
              translate="no"
              onClick={() => {
                setIsSuccess(false);
                reset();
              }}
            >
              {isGoogleUserWithoutPassword
                ? t("profile.setAnotherPassword")
                : t("profile.changePasswordAgain")}
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box className="notranslate" translate="no">
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, mb: 3 }}
        className="notranslate"
        translate="no"
      >
        {isGoogleUserWithoutPassword
          ? t("profile.setPasswordTitle")
          : t("profile.changePasswordTitle")}
      </Typography>

      {isGoogleUserWithoutPassword && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          className="notranslate"
          translate="no"
        >
          {t("profile.googleUserNoPassword")}
        </Alert>
      )}

      {!isGoogleUserWithoutPassword && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          className="notranslate"
          translate="no"
        >
          {t("profile.dontRememberPassword")}{" "}
          <Link
            component="button"
            onClick={async () => {
              // Log out first, then navigate to forgot password
              await logout();
              navigate("/forgot-password");
            }}
            sx={{ cursor: "pointer", textDecoration: "underline" }}
          >
            {t("profile.resetItHere")}
          </Link>
        </Alert>
      )}

      <Card>
        <CardContent>
          <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            {!isGoogleUserWithoutPassword && (
              <TextField
                {...register("currentPassword" as any)}
                label={t("profile.currentPassword")}
                type={showCurrentPassword ? "text" : "password"}
                fullWidth
                margin="normal"
                error={!!currentPasswordError}
                helperText={currentPasswordError?.message}
                disabled={isLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowCurrentPassword(!showCurrentPassword)
                        }
                        edge="end"
                      >
                        {showCurrentPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            )}

            <TextField
              {...register("newPassword" as any)}
              label={t("profile.newPassword")}
              type={showNewPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              error={!!errors.newPassword}
              helperText={errors.newPassword?.message || t("validation.passwordMinSimple")}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              {...register("confirmNewPassword" as any)}
              label={t("profile.confirmNewPassword")}
              type={showConfirmPassword ? "text" : "password"}
              fullWidth
              margin="normal"
              error={!!errors.confirmNewPassword}
              helperText={errors.confirmNewPassword?.message}
              disabled={isLoading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                disabled={isLoading}
                className="notranslate"
                translate="no"
                startIcon={
                  isLoading ? <CircularProgress size={20} /> : <Lock />
                }
              >
                {isLoading
                  ? t("profile.processingBtn")
                  : isGoogleUserWithoutPassword
                    ? t("profile.setPasswordTitle")
                    : t("profile.changePasswordTitle")}
              </Button>
              <Button
                variant="outlined"
                size="large"
                className="notranslate"
                translate="no"
                onClick={() => {
                  reset();
                }}
                disabled={isLoading}
              >
                {t("profile.cancel")}
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProfileChangePassword;
